import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shirt } from '../../components/Shirt';
import { ProductThumb } from '../../components/ProductGallery';
import { PaczkomatPicker } from '../../components/PaczkomatPicker';
import { PhoneInput } from '../../components/PhoneInput';
import { useShop } from '../ShopState';
import { ProductSpec } from '../ProductSpec';
import { StepIndicator } from '../StepIndicator';
import { COLOR_META } from '../colors';
import { quotaExceededMessage } from '../quotaMessage';
import { createOrder, createStripeSession } from '../../api/orders';
import { ApiError } from '../../api/client';
import { formatZl } from '../../types';
import { groszeToZl } from '../../util/money';
import { parcelSizeForShirtCount, shippingCostGrosze } from '../../util/parcelSize';
import { SHIPPING_COUNTRIES } from '../../util/shippingCountries';

export function Checkout() {
  const navigate = useNavigate();
  const { cart, cartTotalQty, cartTotalGrosze, checkout, setCheckout, verification, setVerification, setLastOrder, catalog, markShopClosed } = useShop();

  useEffect(() => {
    if (cart.length === 0) navigate('/sklep', { replace: true });
  }, [cart.length, navigate]);

  if (catalog.status !== 'ready') {
    return <div style={loadingStyle}>Wczytywanie…</div>;
  }
  if (cart.length === 0) {
    return <div style={loadingStyle}>Twój koszyk jest pusty.</div>;
  }
  const { shopStatus, products } = catalog;
  const itemSubtotal = groszeToZl(cartTotalGrosze);
  // This order joins a parcel an earlier order for the same external order already paid for, so
  // there is no method to pick, no address to type and nothing to charge. The server decides this
  // again at creation; see shipping_plan_changed below.
  const ridesAlong = verification?.shippingMode === 'ride_along';
  // Foreign parcels are quoted per destination and booked with a carrier after the fact, so
  // checkout takes the shirts alone and collects a country instead of an address.
  const goesAbroad = !ridesAlong && checkout.method === 'zagranica';
  // Same rule the API charges by (apps/api/src/util/parcelSize.ts): the gabaryt follows the
  // shirt count, so the price moves as the cart grows.
  const parcelSize = parcelSizeForShirtCount(cartTotalQty);
  const shippingCost = ridesAlong
    ? 0
    : groszeToZl(shippingCostGrosze(shopStatus, checkout.method, parcelSize));
  const total = itemSubtotal + shippingCost;

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const lastNameMissing = !checkout.lastName.trim();
  const emailMissing = !checkout.emailValid;
  const paczkomatMissing = !ridesAlong && checkout.method === 'paczkomat' && !checkout.paczkomat;
  const phoneMissing = !checkout.phoneValid;
  const countryMissing = goesAbroad && !checkout.country;
  // POST /orders requires street, postalCode and city to be non-empty for kurier, so without this
  // the button stays enabled and the only feedback is a 400 after submitting.
  const courierMissing =
    !ridesAlong &&
    checkout.method === 'kurier' &&
    (!checkout.courier ||
      !checkout.courier.street.trim() ||
      !checkout.courier.postalCode.trim() ||
      !checkout.courier.city.trim());
  const cannotSubmit =
    lastNameMissing ||
    emailMissing ||
    paczkomatMissing ||
    courierMissing ||
    countryMissing ||
    phoneMissing;
  const cannotSubmitReason = lastNameMissing
    ? 'Podaj nazwisko, aby kontynuować.'
    : emailMissing
      ? 'Podaj poprawny adres email, aby kontynuować.'
      : phoneMissing
        ? 'Podaj poprawny numer telefonu, aby kontynuować.'
        : paczkomatMissing
          ? 'Wybierz paczkomat, aby kontynuować.'
          : countryMissing
            ? 'Wybierz kraj docelowy, aby kontynuować.'
            : courierMissing
              ? 'Podaj ulicę, kod pocztowy i miasto, aby kontynuować.'
              : null;

  const submit = async () => {
    if (lastNameMissing) {
      setSubmitError('Podaj nazwisko.');
      return;
    }
    if (emailMissing) {
      setSubmitError('Podaj poprawny adres email.');
      return;
    }
    if (paczkomatMissing) {
      setSubmitError('Wybierz paczkomat z mapy lub wyszukiwarki.');
      return;
    }
    if (courierMissing) {
      setSubmitError('Podaj ulicę, kod pocztowy i miasto dla przesyłki kurierskiej.');
      return;
    }
    if (countryMissing) {
      setSubmitError('Wybierz kraj, do którego ma trafić paczka.');
      return;
    }
    if (phoneMissing) {
      setSubmitError('Podaj poprawny numer telefonu.');
      return;
    }
    if (!verification) {
      setSubmitError('Weryfikacja wygasła - podaj numer zamówienia jeszcze raz.');
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const customerName = [verification.firstName, checkout.lastName]
        .map((s) => s.trim())
        .filter(Boolean)
        .join(' ');
      const created = await createOrder({
        customerName,
        email: checkout.email.trim(),
        phone: checkout.phone,
        externalOrderId: verification.externalOrderId,
        firstName: verification.firstName,
        items: cart.map((row) => ({
          product_id: row.productId,
          cut: row.cut,
          size: row.size,
          qty: row.qty,
        })),
        // A ride-along sends neither: the server takes both from the parcel it is joining, and a
        // method sent from here would just be ignored.
        shipping_method: ridesAlong ? undefined : checkout.method,
        shipping_address: ridesAlong
          ? {}
          : {
              paczkomat: checkout.method === 'paczkomat' ? checkout.paczkomat : undefined,
              courier: checkout.method === 'kurier' ? checkout.courier : undefined,
              international: goesAbroad ? { country: checkout.country } : undefined,
            },
        customer_comment_text: checkout.comment.trim() || undefined,
      });
      // Straight to Stripe. Stopping on a confirmation page first leaves the buyer on a
      // screen that reads like a receipt for an order nobody has paid for.
      try {
        const session = await createStripeSession(created.id, created.accessToken);
        window.location.assign(session.url);
        return;
      } catch (sessionErr) {
        console.error('[stripe] session failed at checkout', sessionErr);
      }
      // The session is reset on arrival, not here: clearing the gate while this route is still
      // mounted sends it straight back to the gate, because the router applies its own navigation
      // as a transition that an urgent state update outranks.
      navigate(`/sklep/sukces/${created.id}?t=${encodeURIComponent(created.accessToken)}`, {
        state: { placedOrderId: created.id },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        // The allowance is re-derived under a row lock at creation, so this can fire even though
        // the gate said there was room: another order against the same external order id got
        // there first. Report the count the server just computed, not the stale one in session.
        if (err.message === 'quota_exceeded') {
          setSubmitError(quotaExceededMessage(err.body));
        } else if (err.message === 'shop_closed') {
          markShopClosed();
        } else if (err.message === 'external_order_not_found') {
          setSubmitError('Nie rozpoznajemy tego zamówienia - zweryfikuj je ponownie.');
        } else if (err.message === 'shipping_plan_changed' || err.message === 'shipping_closed') {
          // Another order for the same external order landed or was cancelled while this one was
          // being filled in, so the delivery step on screen is not the one the shop would apply.
          // Re-running the gate is what redraws it.
          setVerification(null);
          setSubmitError('Wysyłka tego zamówienia się zmieniła - zweryfikuj zamówienie jeszcze raz.');
        } else if (err.message === 'invalid_paczkomat') {
          setCheckout({ paczkomat: undefined });
          setSubmitError('Ten paczkomat nie jest dostępny - wybierz inny.');
        } else if (err.message === 'paczkomat_validation_unavailable') {
          setSubmitError('InPost chwilowo niedostępny - spróbuj za chwilę.');
        } else if (err.message === 'stripe_unavailable') {
          setSubmitError(
            'Płatności są chwilowo niedostępne - nie możemy teraz przyjąć zamówienia. Spróbuj za chwilę.',
          );
        } else if (err.message === 'invalid_phone') {
          setSubmitError('Numer telefonu jest nieprawidłowy.');
        } else if (err.message === 'missing_country' || err.message === 'invalid_country') {
          setSubmitError('Wybierz kraj z listy, aby wysłać zamówienie za granicę.');
        } else {
          setSubmitError('Nie udało się złożyć zamówienia.');
        }
      } else {
        setSubmitError('Nie udało się złożyć zamówienia.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 28px',
          borderBottom: '2px solid var(--shop-fg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link
            to="/"
            style={{ display: 'flex', alignItems: 'center', gap: 16, color: 'inherit', textDecoration: 'none' }}
          >
            <img src="/logo.png" alt="" style={{ width: 40, height: 40 }} />
            <div className="display" style={{ fontSize: 16, letterSpacing: '.1em' }}>
              OPERACJA TENERYFA
            </div>
          </Link>
        </div>
        <StepIndicator current={3} />
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.4fr 1fr', minHeight: 0 }}>
        <div style={{ padding: '32px 56px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div className="display" style={{ fontSize: 40, lineHeight: 0.95 }}>
            DOSTAWA & PŁATNOŚĆ
          </div>

          <Section number="①" label="ZAMÓWIENIE">
            <div
              style={{
                padding: 14,
                border: '2px solid var(--shop-fg)',
                background: 'rgba(var(--shop-muted-rgb),.04)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 12,
                  letterSpacing: '.14em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: 'var(--orange)',
                    display: 'inline-block',
                  }}
                />
                ZWERYFIKOWANO · {verification?.firstName?.toUpperCase() ?? '-'}
              </div>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: '8px 12px',
                  background: 'transparent',
                  border: '1px solid rgba(var(--shop-muted-rgb),.3)',
                  color: 'var(--shop-fg)',
                  fontFamily: 'var(--display)',
                  fontSize: 11,
                  letterSpacing: '.1em',
                  cursor: 'pointer',
                }}
              >
                ZMIEŃ
              </button>
            </div>
          </Section>

          <Section number="②" label="KONTAKT">
            <div className="field">
              <label htmlFor="d-lastname">Nazwisko</label>
              <input
                id="d-lastname"
                className="input"
                value={checkout.lastName}
                onChange={(e) => setCheckout({ lastName: e.target.value })}
                autoComplete="family-name"
              />
            </div>
            <div className="field">
              <label htmlFor="d-email">Email</label>
              <input
                id="d-email"
                className="input"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={checkout.email}
                onChange={(e) => {
                  const email = e.target.value;
                  setCheckout({
                    email,
                    emailValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
                  });
                }}
              />
            </div>
            <div className="field">
              <label htmlFor="d-phone">Telefon</label>
              <PhoneInput
                id="d-phone"
                variant="shop"
                scope={goesAbroad ? 'world' : 'inpost'}
                value={checkout.phone}
                onChange={(phone, phoneValid) => setCheckout({ phone, phoneValid })}
              />
            </div>
          </Section>

          <Section number="③" label="WYSYŁKA">
            {ridesAlong ? (
              <div
                style={{
                  border: '2px solid var(--orange)',
                  background: 'rgba(255,107,26,.08)',
                  padding: 14,
                }}
              >
                <div
                  className="display"
                  style={{ fontSize: 15, letterSpacing: '.08em', color: 'var(--orange)' }}
                >
                  DOŁĄCZAMY DO TWOJEJ PACZKI
                </div>
                <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
                  Te koszulki spakujemy razem z Twoim wcześniejszym zamówieniem i wyślemy pod ten
                  sam adres. Nie płacisz za wysyłkę drugi raz.
                </div>
              </div>
            ) : (
              <>
            {/* auto-fit rather than a fixed 1fr 1fr 1fr: the desktop layout starts at 640px, where
                three Anton labels this long would be unreadable. Wide viewports get the three
                peers in one row, narrow ones fall back to two or one. */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 10,
              }}
            >
              {(
                [
                  {
                    id: 'paczkomat' as const,
                    name: 'PACZKOMAT INPOST',
                    price: formatZl(groszeToZl(shippingCostGrosze(shopStatus, 'paczkomat', parcelSize))),
                  },
                  {
                    id: 'kurier' as const,
                    name: 'KURIER INPOST',
                    price: formatZl(groszeToZl(shippingCostGrosze(shopStatus, 'kurier', parcelSize))),
                  },
                  {
                    id: 'zagranica' as const,
                    name: 'WYSYŁKA ZAGRANICZNA',
                    price: 'WYCENA INDYWIDUALNA',
                  },
                ]
              ).map((m) => {
                const active = checkout.method === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setCheckout({ method: m.id })}
                    style={{
                      textAlign: 'left',
                      padding: '14px 16px',
                      cursor: 'pointer',
                      // Without this a long label sets the track's min-content width and the
                      // three columns stop being equal.
                      minWidth: 0,
                      border: active ? '2px solid var(--orange)' : '2px solid var(--shop-fg)',
                      background: active ? 'rgba(255,107,26,.1)' : 'transparent',
                      color: 'var(--shop-fg)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 4,
                    }}
                  >
                    <span className="display" style={{ fontSize: 16, color: active ? 'var(--orange)' : 'var(--shop-fg)' }}>
                      {m.name}
                    </span>
                    <span className="mono" style={{ fontSize: 12, color: active ? 'var(--orange)' : 'var(--shop-fg)' }}>
                      {m.price.toUpperCase()}
                    </span>
                  </button>
                );
              })}
            </div>

            {checkout.method === 'paczkomat' && (
              <div style={{ marginTop: 12 }}>
                <PaczkomatPicker
                  value={checkout.paczkomat ?? null}
                  onChange={(next) => setCheckout({ paczkomat: next ?? undefined })}
                />
              </div>
            )}
            {checkout.method === 'kurier' && (
              <div
                style={{
                  marginTop: 12,
                  border: '2px dashed rgba(var(--shop-muted-rgb),.3)',
                  padding: 14,
                  display: 'grid',
                  gap: 8,
                }}
              >
                <input
                  className="input"
                  placeholder="Ulica i numer"
                  value={checkout.courier?.street ?? ''}
                  onChange={(e) =>
                    setCheckout({
                      courier: {
                        street: e.target.value,
                        postalCode: checkout.courier?.postalCode ?? '',
                        city: checkout.courier?.city ?? '',
                      },
                    })
                  }
                />
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
                  <input
                    className="input"
                    placeholder="00-000"
                    value={checkout.courier?.postalCode ?? ''}
                    onChange={(e) =>
                      setCheckout({
                        courier: {
                          street: checkout.courier?.street ?? '',
                          postalCode: e.target.value,
                          city: checkout.courier?.city ?? '',
                        },
                      })
                    }
                  />
                  <input
                    className="input"
                    placeholder="Miasto"
                    value={checkout.courier?.city ?? ''}
                    onChange={(e) =>
                      setCheckout({
                        courier: {
                          street: checkout.courier?.street ?? '',
                          postalCode: checkout.courier?.postalCode ?? '',
                          city: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>
            )}
            {checkout.method === 'zagranica' && (
              <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
                <div
                  style={{
                    border: '2px solid var(--orange)',
                    background: 'rgba(255,107,26,.08)',
                    padding: 14,
                  }}
                >
                  <div
                    className="display"
                    style={{ fontSize: 15, letterSpacing: '.08em', color: 'var(--orange)' }}
                  >
                    WYSYŁKĘ USTALAMY INDYWIDUALNIE
                  </div>
                  <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
                    Teraz płacisz tylko za koszulki. Po zaksięgowaniu wpłaty napiszemy do Ciebie,
                    żeby ustalić sposób i koszt wysyłki do wybranego kraju.
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="d-country">Kraj docelowy</label>
                  <select
                    id="d-country"
                    className="input"
                    value={checkout.country}
                    onChange={(e) => setCheckout({ country: e.target.value })}
                  >
                    <option value="">— wybierz kraj —</option>
                    {SHIPPING_COUNTRIES.map((country) => (
                      <option key={country.iso} value={country.iso}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="d-comment">Uwagi do wysyłki (opcjonalnie)</label>
                  <textarea
                    id="d-comment"
                    className="input"
                    rows={3}
                    value={checkout.comment}
                    onChange={(e) => setCheckout({ comment: e.target.value })}
                    placeholder="Miasto, preferowany przewoźnik, termin — cokolwiek, co pomoże nam wycenić paczkę."
                  />
                </div>
              </div>
            )}
              </>
            )}
          </Section>
        </div>

        <div
          style={{
            background: 'var(--shop-card-bg)',
            borderLeft: '2px solid var(--shop-fg)',
            padding: '32px 36px',
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div className="display" style={{ fontSize: 22, letterSpacing: '.06em' }}>
              PODSUMOWANIE
            </div>
            <div className="mono" style={{ fontSize: 11, color: 'rgba(var(--shop-muted-rgb),.6)', letterSpacing: '.14em' }}>
              {cartTotalQty} {cartTotalQty === 1 ? 'KOSZULKA' : cartTotalQty < 5 ? 'KOSZULKI' : 'KOSZULEK'}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {cart.map((row) => {
              const meta = COLOR_META[row.color];
              const product = products.find((p) => p.id === row.productId);
              return (
                <div key={row.id} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ width: 138, height: 138, border: '1px solid var(--shop-fg)', flexShrink: 0 }}>
                    <ProductThumb
                      images={product?.images ?? []}
                      alt={product?.name ?? 'Koszulka'}
                      fallback={<Shirt color={row.color} cut={row.cut} />}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="display" style={{ fontSize: 14 }}>
                      KOSZULKA · {meta.label}
                    </div>
                    <div
                      className="mono"
                      style={{
                        fontSize: 11,
                        color: 'rgba(var(--shop-muted-rgb),.6)',
                        letterSpacing: '.1em',
                        marginTop: 2,
                      }}
                    >
                      {row.cut.toUpperCase()} · {row.size} · {row.qty} SZT
                    </div>
                    <ProductSpec product={product} fontSize={10.5} />
                  </div>
                  <div className="display" style={{ fontSize: 14, flexShrink: 0 }}>
                    {formatZl(groszeToZl(row.unitPriceGrosze * row.qty))}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ borderTop: '1px dashed rgba(var(--shop-muted-rgb),.3)' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>Suma koszulek</span>
            <span className="mono">{formatZl(itemSubtotal)}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>
              {ridesAlong
                ? 'Wysyłka (dołączona do paczki)'
                : goesAbroad
                  ? 'Wysyłka zagraniczna'
                  : checkout.method === 'paczkomat'
                    ? 'Paczkomat InPost'
                    : 'Kurier InPost'}
            </span>
            <span className="mono">
              {goesAbroad
                ? 'USTALANA INDYWIDUALNIE'
                : ridesAlong
                  ? '0,00 ZŁ'
                  : formatZl(shippingCost)}
            </span>
          </div>

          <div style={{ borderTop: '2px solid var(--shop-fg)' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="display" style={{ fontSize: 18 }}>
              RAZEM
            </span>
            <span className="display" style={{ fontSize: 36, color: 'var(--orange)' }}>
              {formatZl(total).toUpperCase()}
            </span>
          </div>

          {goesAbroad && (
            <div
              className="mono"
              style={{
                marginTop: -8,
                fontSize: 11,
                lineHeight: 1.5,
                color: 'rgba(var(--shop-muted-rgb),.75)',
              }}
            >
              KWOTA OBEJMUJE SAME KOSZULKI. KOSZT WYSYŁKI ZA GRANICĘ USTALIMY Z TOBĄ OSOBNO.
            </div>
          )}

          <button
            className="btn full"
            style={{
              fontSize: 20,
              padding: '18px 22px',
              opacity: submitting || cannotSubmit ? 0.5 : 1,
              cursor: submitting || cannotSubmit ? 'not-allowed' : 'pointer',
            }}
            onClick={submit}
            disabled={submitting || cannotSubmit}
          >
            {submitting ? 'WYSYŁANIE…' : 'ZŁÓŻ ZAMÓWIENIE →'}
          </button>

          {submitError ? (
            <div
              className="mono"
              style={{
                marginTop: -8,
                padding: '8px 10px',
                border: '1px dashed var(--orange)',
                color: 'var(--orange)',
                fontSize: 12,
              }}
            >
              {submitError}
            </div>
          ) : cannotSubmit && !submitting && cannotSubmitReason ? (
            <div
              className="mono"
              style={{
                marginTop: -8,
                padding: '8px 10px',
                border: '1px dashed rgba(var(--shop-muted-rgb),.4)',
                color: 'rgba(var(--shop-muted-rgb),.9)',
                fontSize: 12,
              }}
            >
              {cannotSubmitReason}
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            {['BLIK', 'KARTA', 'PRZELEWY24'].map((m) => (
              <span
                key={m}
                className="mono"
                style={{
                  fontSize: 10,
                  padding: '4px 8px',
                  border: '1px solid rgba(var(--shop-muted-rgb),.3)',
                  letterSpacing: '.14em',
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({
  number,
  label,
  children,
}: {
  number: string;
  label: string;
  children: import('react').ReactNode;
}) {
  return (
    <div>
      <div
        className="display"
        style={{ fontSize: 13, letterSpacing: '.14em', color: 'var(--orange)', marginBottom: 10 }}
      >
        {number} {label}
      </div>
      {children}
    </div>
  );
}

const loadingStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  opacity: 0.6,
} as const;
